'use client';

import { useRef } from 'react';
import clsx from 'clsx';
import { useInView } from 'motion/react';
import { getOptimizedImageUrl } from '@bgzt/media';

import { IcChevronRight, Typography, theme } from '@bgzt/ui';

import { ProductDataSimpleCategory, ProductDataV2 } from '~/apis/search';
import { useFavorite } from '~/hooks';
import { ViewType, getSizeByViewType } from '../Image/helpers';
import { Brand } from '~/apis/mdms';

import { Spacing, Image, AppDownloadTrigger } from '~/components';

import { formatPrice } from '~/utils';
// import { PROMOTION_2024_12 } from '~/app/_constants';
// import { checkPromotionSchedule } from '~/app/_utils';

import IcFavoriteOn from '~/public/assets/icons/ic_fav_on.svg';
import IcFavoriteOff from '~/public/assets/icons/ic_fav_off.svg';
import MercariBadge from '~/public/assets/icons/ic_mercari_badge.svg';

import globalStyles from '~/styles/utils.module.css';
import styles from './ProductItem.module.scss';

type Props = {
  // product: ComBunjangSearchApiServiceProductModelProductData;
  brand?: Brand;
  viewType?: ViewType;
  onFavoriteClick?: (value: boolean) => void;
  onBrandCategoryShortCutClick?: () => void;

  pid: number;
  image: string;
  name: string;
  location?: string;
  updatedBefore?: string;
  price: number;
  status: string;
  faved: boolean;
  category?: ProductDataSimpleCategory;
  snackbarBottomOffset?: number;
  discountRate?: number;
};

const STATUS: Record<ProductDataV2['status'], string> = {
  SELLING: '',
  RESERVED: '예약중',
  DELETED: '삭제\n상품',
  SOLD_OUT: '판매\n완료',
};

export const ProductItem = ({
  // product,
  brand,
  viewType = '2-column',
  onFavoriteClick,
  onBrandCategoryShortCutClick,
  pid,
  image,
  name,
  location,
  updatedBefore,
  price,
  status,
  faved,
  category,
  snackbarBottomOffset,
  discountRate,
}: Props) => {
  const { toggle, isFavorite } = useFavorite(faved);
  const { aspectRatio, width, height } = getSizeByViewType(viewType);
  const containerRef = useRef(null);
  const isInView = useInView(containerRef, { once: true });
  const optimizedImageUrl = getOptimizedImageUrl(image, 320, { resolutionMultiplier: 1 });
  // const isPromotionSchedule = checkPromotionSchedule(
  //   PROMOTION_2024_12.SCHEDULE.START_DATE,
  //   PROMOTION_2024_12.SCHEDULE.END_DATE,
  // );

  return (
    <div className={styles.container} ref={containerRef}>
      <div style={{ aspectRatio }} className={styles.productImageContainer}>
        <Image
          className={clsx([styles.productImage, isInView && styles.isInView])}
          src={optimizedImageUrl}
          alt={name}
          width={width}
          height={height}
        />

        <div className={clsx(styles.gradientOverlay)} />

        <AppDownloadTrigger>
          <div
            className={styles.favorite}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              toggle(pid, String(image), { snackbarBottomOffset });
              onFavoriteClick?.(!isFavorite);
            }}
          >
            {isFavorite ? <IcFavoriteOn /> : <IcFavoriteOff />}
          </div>
        </AppDownloadTrigger>
        <MercariBadge className={styles.badge} />

        {STATUS?.[status as ProductDataV2['status']] && (
          <div className={styles.statusContainer}>
            <Typography variant="L7" color="white">
              {STATUS[status as ProductDataV2['status']]}
            </Typography>
          </div>
        )}
      </div>

      <Spacing height={8} />

      <div className={clsx(styles.priceContainer, { [styles.threeColumn]: viewType === '3-column' })}>
        {discountRate && (
          <Typography variant={viewType === '3-column' ? 'L7' : 'L6'} color="red-500">
            {discountRate}%
          </Typography>
        )}
        <Typography variant={viewType === '3-column' ? 'L7' : 'L6'} color="gray-900">
          {viewType === '3-column' ? formatPrice(price) : `${price.toLocaleString()}원`}
        </Typography>
      </div>

      <Spacing height={4} />

      <Typography
        variant={viewType === '3-column' ? 'L10' : 'L8'}
        color="gray-600"
        as="p"
        className={clsx(styles.wordBreakAll, {
          [styles.productNameFull]: viewType === 'full',
          [globalStyles.singleLineEllipticalText]: viewType === 'full',
          [styles.productNameTwoColumn]: viewType === '2-column',
          [globalStyles.singleLineEllipticalText]: viewType === '2-column',
          [globalStyles.doubleLineEllipticalText]: viewType === '3-column',
        })}
      >
        {name}
      </Typography>

      <Spacing height={4} />

      <Typography
        as="div"
        variant={viewType === '3-column' ? 'L13' : 'L12'}
        color="gray-300"
        className={globalStyles.singleLineEllipticalText}
      >
        {location} ・ {updatedBefore}
      </Typography>

      {category && (
        <>
          <Spacing height={6} />

          <div
            className={styles.brandCategoryShortCut}
            onClick={(e) => {
              e.preventDefault();
              onBrandCategoryShortCutClick?.();
            }}
          >
            <Spacing height={8} />
            <div className={styles.brandCategoryShortCutText}>
              <Typography variant="L11" color="gray-700" className={globalStyles.singleLineEllipticalText}>
                {`${brand ? brand.nameKor + ' ・ ' : ''}${category?.name.split('>').slice(-1)[0]}`}
              </Typography>
              <IcChevronRight size={10} fill={theme.colors['gray-500']} />
            </div>
          </div>
        </>
      )}
    </div>
  );
};
